import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type SearchMode = 'quick' | 'regular' | 'deep';

interface ChatSettingsContextType {
  showCitations: boolean;
  setShowCitations: (show: boolean) => void;
  searchMode: SearchMode;
  setSearchMode: (mode: SearchMode) => void;
}

const ChatSettingsContext = createContext<ChatSettingsContextType | undefined>(undefined);

export const useChatSettings = () => {
  const context = useContext(ChatSettingsContext);
  if (!context) {
    throw new Error("useChatSettings must be used within ChatSettingsProvider");
  }
  return context;
};

export const ChatSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [showCitations, setShowCitationsState] = useState(() => {
    const saved = localStorage.getItem('pathwork_show_citations');
    return saved ? JSON.parse(saved) : false;
  });

  const [searchMode, setSearchModeState] = useState<SearchMode>(() => {
    const saved = localStorage.getItem('pathwork_search_mode');
    return (saved as SearchMode) || 'regular';
  });

  useEffect(() => {
    localStorage.setItem('pathwork_show_citations', JSON.stringify(showCitations));
  }, [showCitations]);

  useEffect(() => {
    localStorage.setItem('pathwork_search_mode', searchMode);
  }, [searchMode]);

  const setShowCitations = (show: boolean) => {
    setShowCitationsState(show);
  };

  const setSearchMode = (mode: SearchMode) => {
    setSearchModeState(mode);
  };

  return (
    <ChatSettingsContext.Provider value={{ showCitations, setShowCitations, searchMode, setSearchMode }}>
      {children}
    </ChatSettingsContext.Provider>
  );
};
